import { HttpClient, HttpDownloadProgressEvent, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Discriminated union of events emitted while streaming an Emily chat reply.
 *
 * - `session` — emitted once, first, by {@link EmilyChatService.createSession}. Carries the
 *   persisted `sessionId` to use for follow-up turns. Never emitted by `sendMessage`.
 * - `chunk` — a raw reply fragment. Concatenate `text` in arrival order to build the reply.
 * - `done` — normal terminal event; the stream completes right after.
 * - `error` — mid-stream model/stream failure (arrives with HTTP 200, then the stream closes
 *   cleanly). Distinct from pre-stream HTTP errors (401/403/404), which surface as the
 *   Observable's `error` channel via an {@link HttpErrorResponse}.
 */
export type ChatStreamEvent =
  | { type: 'session'; sessionId: number }
  | { type: 'chunk'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

/**
 * Error thrown on the Observable's error channel for a pre-stream HTTP failure.
 *
 * Carries the originating HTTP `status` so callers can distinguish a truly unusable session
 * (401/403/404 — abandon it) from a transient failure (0 network/CORS/aborted, timeout, 5xx —
 * keep the session and retry, preserving conversation context).
 */
export class EmilyChatError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'EmilyChatError';
  }
}

/**
 * Client for the EMS AI ("Emily") streaming chat endpoints (backend EMS-123).
 *
 * Transport: SSE-over-POST. Native `EventSource` cannot POST, so this uses Angular's HttpClient
 * fetch backend with progress events (`observe: 'events'`, `reportProgress: true`,
 * `responseType: 'text'`). `HttpEventType.DownloadProgress.partialText` carries the accumulated
 * response body as it streams; `HttpEventType.Response` marks the end. Staying on HttpClient
 * keeps `addContextInterceptor` (relative URLs auto-prefixed with `ems-api/`) and lets unsubscribe
 * abort the in-flight request. Requires `withFetch()` in `provideHttpClient` (app.config.ts).
 *
 * Auth: every request sends the JSESSIONID cookie (`withCredentials: true`) — NOT a bearer token.
 *
 * SSE event contract (per record, separated by a blank line):
 * - default (unnamed) event → reply chunk, emitted as `{ type: 'chunk' }`.
 * - `event: session` → `{ "sessionId": <long> }`, emitted as `{ type: 'session' }` (create only).
 * - `event: done` → terminal, emitted as `{ type: 'done' }`.
 * - `event: error` → mid-stream failure message, emitted as `{ type: 'error' }`.
 *
 * Error model:
 * - 401 (no/invalid session), 403 (session owned by another user), 404 (session not found in
 *   tenant) are checked BEFORE streaming and arrive as an {@link HttpErrorResponse} on the
 *   Observable's error channel.
 * - Model failures AFTER streaming starts arrive as an `error` SSE event with HTTP 200.
 *
 * Backend endpoints:
 * - POST `ai/chat/sessions/stream` — create session + stream first reply (used here).
 * - POST `ai/chat/sessions/{id}/messages/stream` — follow-up turn, context-aware (used here).
 * - POST `ai/chat/stream` — stateless streaming, no persistence (not used).
 * - POST `ai/chat` — single-shot non-streaming JSON `{ reply }` (not used).
 *
 * Two-call flow for a new conversation: {@link createSession} → grab `sessionId` from the
 * `session` event → subsequent turns via {@link sendMessage}. No GET/history endpoint exists yet;
 * the UI must retain streamed chunks client-side for the live session.
 */
@Injectable({ providedIn: 'root' })
export class EmilyChatService {

  #http: HttpClient = inject(HttpClient);

  /**
   * Create a new persisted chat session from the first message and stream the reply.
   *
   * POST `ai/chat/sessions/stream`, body `{ message }`. The message becomes the session title
   * (truncated to 120 chars server-side). Emits a `session` event first (save the `sessionId`),
   * then `chunk` events, then `done`.
   */
  createSession(message: string): Observable<ChatStreamEvent> {
    return this.#stream('ai/chat/sessions/stream', { message });
  }

  /**
   * Post a follow-up message to an existing session and stream the context-aware reply.
   *
   * POST `ai/chat/sessions/{sessionId}/messages/stream`, body `{ message }`. Prior turns are
   * replayed to the model server-side. Emits `chunk` events then `done` (no `session` event).
   *
   * @param sessionId id from the `session` event of a prior {@link createSession} call.
   */
  sendMessage(sessionId: number, message: string): Observable<ChatStreamEvent> {
    return this.#stream(`ai/chat/sessions/${sessionId}/messages/stream`, { message });
  }

  #stream(url: string, body: { message: string }): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>((subscriber) => {
      let lastLen = 0;
      let buffer = '';

      const flushRecords = (final: boolean) => {
        const records = buffer.split('\n\n');
        // Mid-stream the last element is a partial record (hold it back); at end-of-stream
        // every element is complete.
        buffer = final ? '' : (records.pop() ?? '');
        for (const record of records) {
          const event = this.#parseRecord(record);
          if (event) {
            subscriber.next(event);
          }
        }
      };

      const inner = this.#http
        .post(url, body, {
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
          withCredentials: true,
        })
        .pipe(
          catchError((errorResponse: HttpErrorResponse) =>
            // status is 0 for network/CORS/aborted — treat as transient (see EmilyChatError).
            throwError(() => new EmilyChatError(errorResponse.status, this.#errorMessage(errorResponse))),
          ),
        )
        .subscribe({
          next: (httpEvent: HttpEvent<string>) => {
            if (httpEvent.type === HttpEventType.DownloadProgress) {
              const partialText = (httpEvent as HttpDownloadProgressEvent).partialText ?? '';
              buffer += partialText.slice(lastLen);
              lastLen = partialText.length;
              flushRecords(false);
            } else if (httpEvent.type === HttpEventType.Response) {
              flushRecords(true);
            }
          },
          error: (error: Error) => subscriber.error(error),
          complete: () => subscriber.complete(),
        });

      return () => inner.unsubscribe();
    });
  }

  /** Parse one SSE record into a {@link ChatStreamEvent}, or `null` for blank/keep-alive records. */
  #parseRecord(record: string): ChatStreamEvent | null {
    const lines = record.split('\n');
    const event = lines.find((l) => l.startsWith('event:'))?.slice(6).trim();
    // Spring's SSE writer emits `data:<content>` with no separator space, so keep the raw
    // remainder verbatim — stripping a leading space would eat token word boundaries.
    const data = lines
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5))
      .join('\n');

    if (event === 'session') {
      try {
        return { type: 'session', sessionId: JSON.parse(data).sessionId };
      } catch {
        return null;
      }
    }
    if (event === 'done') {
      return { type: 'done' };
    }
    if (event === 'error') {
      return { type: 'error', message: data };
    }
    // Default (unnamed) event = reply chunk. Ignore records with no data (e.g. keep-alives).
    return data ? { type: 'chunk', text: data } : null;
  }

  #errorMessage(errorResponse: HttpErrorResponse): string {
    const body = errorResponse.error;
    if (typeof body === 'string' && body) {
      try {
        return JSON.parse(body).message ?? errorResponse.message;
      } catch {
        return body;
      }
    }
    return body?.message ?? errorResponse.message;
  }
}
